import { AgentMessage, ToolEvent } from './model';

// Public display IDs follow Perseus and the existing lechat-agent contract.
export const TOOL_TITLES: Record<string, string> = {
  toolStep: 'agent.thinking.toolStep',
  readApiDocs: 'agent.thinking.readApiDocs',
  findCapabilities: 'agent.thinking.findCapabilities',
  readResources: 'agent.thinking.readResources',
  analyzeData: 'agent.thinking.analyzeData',
  exportWorkbook: 'agent.thinking.exportWorkbook',
  searchWeb: 'agent.thinking.searchWeb',
  readWeb: 'agent.thinking.readWeb',
  prepareChanges: 'agent.thinking.prepareChanges',
  submitChanges: 'agent.thinking.submitChanges',
  askConfirmation: 'agent.thinking.askConfirmation',
  planSteps: 'agent.thinking.planSteps',
  delegateSteps: 'agent.thinking.delegateSteps',
  searchProducts: 'agent.thinking.searchProducts',
  reportAgents: 'agent.thinking.reportAgents',
  marketingPolicies: 'agent.thinking.marketingPolicies',
  policyDetails: 'agent.thinking.policyDetails',
  mercadoReputation: 'agent.thinking.mercadoReputation',
  reportConversations: 'agent.thinking.reportConversations',
  businessReports: 'agent.thinking.businessReports',
  authorizeStore: 'agent.thinking.authorizeStore',
  authorizationRecords: 'agent.thinking.authorizationRecords',
  authorizationRecord: 'agent.thinking.authorizationRecord',
  dashboardStores: 'agent.thinking.dashboardStores',
  organization: 'agent.thinking.organization',
  stores: 'agent.thinking.stores',
  departmentsRoles: 'agent.thinking.departmentsRoles',
  capabilityAccess: 'agent.thinking.capabilityAccess',
  exportLogs: 'agent.thinking.exportLogs',
  storeCount: 'agent.thinking.storeCount',
  storeStates: 'agent.thinking.storeStates',
  departments: 'agent.thinking.departments',
  userPermissions: 'agent.thinking.userPermissions',
  agents: 'agent.thinking.agents',
  productDetails: 'agent.thinking.productDetails',
  voucherDetails: 'agent.thinking.voucherDetails',
  knowledgeStatistics: 'agent.thinking.knowledgeStatistics',
  campaignDetails: 'agent.thinking.campaignDetails',
  campaignConversion: 'agent.thinking.campaignConversion',
  agentHourlyTrend: 'agent.thinking.agentHourlyTrend',
  consultConversionDetails: 'agent.thinking.consultConversionDetails',
  consultConversionTrend: 'agent.thinking.consultConversionTrend',
  chatData: 'agent.thinking.chatData',
  agentPerformance: 'agent.thinking.agentPerformance',
  botGaps: 'agent.thinking.botGaps',
  customerHealth: 'agent.thinking.customerHealth',
  weeklyReport: 'agent.thinking.weeklyReport',
  orderLossAnalysis: 'agent.thinking.orderLossAnalysis',
  orderLossDetails: 'agent.thinking.orderLossDetails',
  orderOverview: 'agent.thinking.orderOverview',
  recoveryFunnel: 'agent.thinking.recoveryFunnel',
  businessOverview: 'agent.thinking.businessOverview',
  paymentReminderGaps: 'agent.thinking.paymentReminderGaps',
  productPerformance: 'agent.thinking.productPerformance',
  responseCapacity: 'agent.thinking.responseCapacity',
  negativeReviewReasons: 'agent.thinking.negativeReviewReasons',
  reviewProblems: 'agent.thinking.reviewProblems',
  conversationSatisfaction: 'agent.thinking.conversationSatisfaction',
  scenarioOpportunities: 'agent.thinking.scenarioOpportunities',
  productConsultScenarios: 'agent.thinking.productConsultScenarios',
  reviewOverview: 'agent.thinking.reviewOverview',
  fulfillmentDateRange: 'agent.thinking.fulfillmentDateRange',
  orderLoss: 'agent.thinking.orderLoss',
  orderLossReasons: 'agent.thinking.orderLossReasons',
  orderRecovery: 'agent.thinking.orderRecovery',
  fulfillmentRank: 'agent.thinking.fulfillmentRank',
  recoveryRank: 'agent.thinking.recoveryRank',
  salesConversion: 'agent.thinking.salesConversion',
  satisfactionDetails: 'agent.thinking.satisfactionDetails',
  storeHealth: 'agent.thinking.storeHealth',
  promotionDetails: 'agent.thinking.promotionDetails',
  agentReceptionScores: 'agent.thinking.agentReceptionScores',
  receptionScoreDetails: 'agent.thinking.receptionScoreDetails',
  receptionScoreList: 'agent.thinking.receptionScoreList',
  receptionScores: 'agent.thinking.receptionScores',
  botAutoReviews: 'agent.thinking.botAutoReviews',
  botCancellationRecovery: 'agent.thinking.botCancellationRecovery',
  botConversations: 'agent.thinking.botConversations',
  botContribution: 'agent.thinking.botContribution',
  queueBotExport: 'agent.thinking.queueBotExport',
  exportBotContribution: 'agent.thinking.exportBotContribution',
  botReplies: 'agent.thinking.botReplies',
  exportBotReplies: 'agent.thinking.exportBotReplies',
  botOrderReminders: 'agent.thinking.botOrderReminders',
  botPaymentReminders: 'agent.thinking.botPaymentReminders',
  botReviewReminders: 'agent.thinking.botReviewReminders',
  botIndependence: 'agent.thinking.botIndependence',
  botReplyRate: 'agent.thinking.botReplyRate',
  topConsultations: 'agent.thinking.topConsultations',
  productRanking: 'agent.thinking.productRanking',
  callApi: 'agent.thinking.callApi',
  toolRunning: 'agent.thinking.toolRunning',
};
export function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
export function formatDuration(value?: number | null): string | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return value < 1000
    ? `${Math.round(value)}ms`
    : value < 10000
    ? `${(value / 1000).toFixed(1)}s`
    : `${Math.round(value / 1000)}s`;
}
export function appendReasoningSegment(
  segments: NonNullable<AgentMessage['reasoning_segments']>,
  sequence: number,
  content: string,
): NonNullable<AgentMessage['reasoning_segments']> {
  if (!content) return segments;
  const last = segments[segments.length - 1];
  return last && sequence === last.end_sequence + 1
    ? [
        ...segments.slice(0, -1),
        { ...last, end_sequence: sequence, content: last.content + content },
      ]
    : [...segments, { sequence, end_sequence: sequence, content }];
}
export function thinkingTimeline(message: AgentMessage) {
  const tools = new Map<string, ToolEvent>();
  for (const tool of message.tool_events) {
    const payload = record(tool.event_payload);
    const key =
      typeof payload.step_id === 'string' && payload.step_id
        ? payload.step_id
        : tool.tool_call_id || tool.id;
    const previous = tools.get(key);
    if (!previous) {
      tools.set(key, tool);
      continue;
    }
    const terminal = (value: ToolEvent) =>
      ['tool.completed', 'tool.failed'].includes(value.event_type);
    const winner = terminal(previous) && !terminal(tool) ? previous : tool;
    const loser = winner === previous ? tool : previous;
    tools.set(key, {
      ...loser,
      ...winner,
      sequence: previous.sequence ?? tool.sequence,
      duration_ms: winner.duration_ms ?? loser.duration_ms,
      event_payload: {
        ...record(loser.event_payload),
        ...record(winner.event_payload),
        display: record(winner.event_payload).display ?? record(loser.event_payload).display,
      },
    });
  }
  type Entry =
    | { kind: 'reasoning'; sequence: number; content: string }
    | { kind: 'tool'; sequence: number; tool: ToolEvent };
  const entries: Entry[] = message.reasoning_segments?.length
    ? message.reasoning_segments
        .filter((s) => s.content.trim())
        .map((s) => ({ ...s, kind: 'reasoning' }))
    : message.reasoning?.trim()
    ? [{ kind: 'reasoning', sequence: -Infinity, content: message.reasoning }]
    : [];
  [...tools.values()].forEach((tool, index) => {
    const sequence = tool.sequence ?? record(tool.event_payload).sequence;
    entries.push({
      kind: 'tool',
      tool,
      sequence: typeof sequence === 'number' && Number.isFinite(sequence) ? sequence : index,
    });
  });
  return entries.sort((a, b) => a.sequence - b.sequence);
}
